import React, { useCallback, useEffect } from 'react';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';
import { CanvasComponent } from './CanvasComponent';

const CANVAS_WIDTH = 720;
const CANVAS_HEIGHT = 1280;

export function ScreenCanvas() {
  const activeScreenId = useSlotScreenGeneratorStore(s => s.activeScreenId);
  const components = useSlotScreenGeneratorStore(s => s.screenComponents[s.activeScreenId] ?? []);
  const selectedId = useSlotScreenGeneratorStore(s => s.selectedComponentId);
  const zoom = useSlotScreenGeneratorStore(s => s.canvasZoom);

  // Keyboard shortcuts for canvas interaction
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const store = useSlotScreenGeneratorStore.getState();
      const { selectedComponentId, activeScreenId: screenId, screenComponents } = store;

      if (e.key === 'Escape') {
        store.selectComponent(null);
        return;
      }

      if (!selectedComponentId) return;

      const comp = screenComponents[screenId]?.find(c => c.id === selectedComponentId);
      if (!comp) return;

      const step = e.shiftKey ? 10 : 1;

      switch (e.key) {
        case 'Delete':
        case 'Backspace':
          if (comp.selectedResultId && !comp.locked) {
            e.preventDefault();
            store.deleteComponentResult(screenId, selectedComponentId, comp.selectedResultId);
          }
          break;
        case 'ArrowUp':
          if (!comp.locked) { e.preventDefault(); store.updateComponentLayout(screenId, selectedComponentId, { y: comp.y - step }); }
          break;
        case 'ArrowDown':
          if (!comp.locked) { e.preventDefault(); store.updateComponentLayout(screenId, selectedComponentId, { y: comp.y + step }); }
          break;
        case 'ArrowLeft':
          if (!comp.locked) { e.preventDefault(); store.updateComponentLayout(screenId, selectedComponentId, { x: comp.x - step }); }
          break;
        case 'ArrowRight':
          if (!comp.locked) { e.preventDefault(); store.updateComponentLayout(screenId, selectedComponentId, { x: comp.x + step }); }
          break;
        case 'Enter':
          e.preventDefault();
          store.openGeneratorView(selectedComponentId);
          break;
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const sorted = [...components].sort((a, b) => a.zIndex - b.zIndex);

  const handleSelect = useCallback((id: string) => {
    useSlotScreenGeneratorStore.getState().selectComponent(id);
  }, []);

  const handleDoubleClick = useCallback((id: string) => {
    useSlotScreenGeneratorStore.getState().openGeneratorView(id);
  }, []);

  const handleMove = useCallback((id: string, x: number, y: number) => {
    const { activeScreenId: screenId } = useSlotScreenGeneratorStore.getState();
    useSlotScreenGeneratorStore.getState().updateComponentLayout(screenId, id, { x, y });
  }, []);

  const handleResize = useCallback((id: string, width: number, height: number, x: number, y: number) => {
    const { activeScreenId: screenId } = useSlotScreenGeneratorStore.getState();
    useSlotScreenGeneratorStore.getState().updateComponentLayout(screenId, id, { x, y, width, height });
  }, []);

  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Only deselect if clicking on the canvas background itself
    if (e.target === e.currentTarget) {
      useSlotScreenGeneratorStore.getState().selectComponent(null);
    }
  }, []);

  return (
    <div
      className="flex-1 overflow-auto bg-zinc-950"
      onClick={handleCanvasClick}
    >
      <div
        className="relative mx-auto"
        style={{
          width: CANVAS_WIDTH * zoom,
          height: CANVAS_HEIGHT * zoom,
          marginTop: 16,
          marginBottom: 16,
        }}
      >
        <div
          className="relative bg-zinc-900 shadow-2xl"
          style={{
            width: CANVAS_WIDTH,
            height: CANVAS_HEIGHT,
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
          }}
          onClick={handleCanvasClick}
        >
          {/* Checkerboard background pattern to indicate transparent area */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.03]"
            style={{
              backgroundImage:
                'linear-gradient(45deg, #888 25%, transparent 25%), ' +
                'linear-gradient(-45deg, #888 25%, transparent 25%), ' +
                'linear-gradient(45deg, transparent 75%, #888 75%), ' +
                'linear-gradient(-45deg, transparent 75%, #888 75%)',
              backgroundSize: '20px 20px',
              backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0',
            }}
          />

          {sorted.map(comp => (
            <CanvasComponent
              key={`${activeScreenId}-${comp.id}`}
              component={comp}
              isSelected={comp.id === selectedId}
              zoom={zoom}
              onSelect={handleSelect}
              onDoubleClick={handleDoubleClick}
              onMove={handleMove}
              onResize={handleResize}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
